/**
 * Pure mappers: real app data records → `CodingScreenState.values` for the project_starburst shell.
 *
 * This module lives OUTSIDE `project_starburst/` on purpose (the folder's written contract —
 * `interface/state.ts` header + README): it is the out-of-folder adapter seam that maps the app's
 * real data into the folder's source-agnostic `PatientInfoData` / `CaseSpecialtyData` /
 * `CodingScreenState` contract. It is PURE — no redux, no fetch, no React — so Jest, Storybook, and
 * the connected adapter all drive it identically.
 *
 * `CMxAPI.*` and `Accelerate.*` are AMBIENT GLOBALS (declared by `@codametrix/ui-type-definitions`,
 * global-ambient via tsconfig `types`) — referenced with NO import. Only the folder's own type-only
 * `CodingScreenState` / `PatientInfoData` are imported, from the interface barrel.
 *
 * Mode-gated namespaces: coding mode → `case.*` contentIds from the coding `CMxAPI.CaseRun`;
 * grading mode → `workitem.*` contentIds from the grade-bench case record
 * (`state.codeBench.activeItem` — declared `CMxAPI.CaseRun`, a snake_case CQA case at runtime).
 */
import { differenceInYears, parseISO } from 'date-fns';
import type {
  BillingLocationData,
  CaseProvidersData,
  CaseSpecialtyData,
  CodingScreenState,
  PatientInfoData,
  PatientTypeInfoData,
  PayerInfoData
} from '../../../project_starburst/interface';

/**
 * Age at date of service, derived from date of birth — the coding-domain convention.
 * Shared by the coding and grading mappers, which both carry a
 * birth date + service date. Returns undefined (-> 'N/A') when a date is
 * missing/unparseable or the result is negative. No wall-clock fallback: derivation
 * stays off `new Date()` so the mapper remains pure and deterministic (E2E-safe).
 * Avoids the buggy `patient_age_by_dos_dob` transformer (returns undefined on its
 * truthy branch).
 */
function ageAtService(
  dateOfBirth: string | null | undefined,
  dateOfService: string | null | undefined
): string | undefined {
  if (dateOfBirth == null || dateOfService == null) {
    return undefined;
  }
  const birth = parseISO(dateOfBirth);
  const service = parseISO(dateOfService);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(service.getTime())) {
    return undefined;
  }
  const years = differenceInYears(service, birth);
  return years >= 0 ? String(years) : undefined;
}

/**
 * Map the primary `CMxAPI.CaseRun` (coding mode) to the source-agnostic patient contract.
 *
 * Identifier fields on `CaseRun` are `CaseNumber` (`{ number, issuer, type }`); take `.number` only
 * (`issuer`/`type` are genuinely null in real data). Every field normalizes `null → undefined`
 * because `PatientInfoData` fields are `string | undefined` while `CaseRun` uses `NullableString`.
 */
export function toPatientInfoFromCaseRun(run: CMxAPI.CaseRun): PatientInfoData {
  return {
    fullName: run.patient_name ?? undefined,
    gender: run.patient_gender ?? undefined,
    dob: run.date_of_birth ?? undefined,
    // Prefer the explicit patient_age (null on real cases -> derive age at service).
    age:
      run.patient_age != null
        ? String(run.patient_age)
        : ageAtService(run.date_of_birth, run.date_of_service),
    mrn: run.patient_identifier?.number ?? undefined,
    accountNumber: run.account_number?.number ?? undefined,
    visitNumber: run.visit_number?.number ?? undefined,
    caseId: run.case_id != null ? String(run.case_id) : undefined
  };
}

export function toBillingLocationFromCaseRun(
  run: CMxAPI.CaseRun
): BillingLocationData {
  return {
    location: run.billing_location ?? undefined,
    department: run.scheduling_department ?? undefined
  };
}

type CaseRunSpecialty = {
  note_specialty_designation?: string | null;
  facts?: {
    is_hospital_service_same_as_claim_specialty?: string | null;
  } | null;
};

export function toCaseSpecialtyFromCaseRun(
  run: CMxAPI.CaseRun
): CaseSpecialtyData {
  const off = run as unknown as CaseRunSpecialty;
  return {
    specialty: off.note_specialty_designation ?? undefined,
    department: run.scheduling_department ?? undefined,
    billingArea: run.billing_area ?? undefined,
    serviceRole:
      off.facts?.is_hospital_service_same_as_claim_specialty ?? undefined,
    serviceLine: run.service_line ?? undefined
  };
}

type CaseRunProviderExtras = {
  admitting_provider_name?: string | null;
  admitting_provider_number?: ProviderIdentifier | null;
  consulting_provider_name?: string | null;
  consulting_provider_number?: ProviderIdentifier | null;
  billing_provider_type?: string | null;
  billing_provider_credentials?: string | null;
  performing_provider_type?: string | null;
  performing_provider_credentials?: string | null;
};

type ProviderIdentifier = {
  readonly number?: string | null;
  readonly issuer?: string | null;
  readonly type?: string | null;
};

type IdentityValue = ProviderIdentifier | string | null | undefined;

type NormalizedIdentity = {
  readonly number: string;
  readonly issuer?: string;
  readonly type?: string;
};

type NamedRoleField = {
  readonly id: string;
  readonly label: string;
  readonly name: string | null | undefined;
  readonly providerType?: string | null;
  readonly credentials?: string | null;
  readonly specialty?: string | null;
  readonly identity?: IdentityValue;
};

type MutableProviderEntry = {
  name: string;
  credentials?: string;
  providerType?: string;
  specialty?: string;
  isAttending: boolean;
  roles: { id: string; label: string }[];
  identityIssuer?: string;
  identityType?: string;
};

function preferFirst(
  current: string | undefined,
  candidate: string | null | undefined
): string | undefined {
  if (current != null) {
    return current;
  }
  return typeof candidate === 'string' && candidate.trim() !== ''
    ? candidate
    : current;
}

function normalizeIdentity(identity: IdentityValue): NormalizedIdentity | undefined {
  if (identity == null) {
    return undefined;
  }
  if (typeof identity === 'string') {
    const number = identity.trim();
    return number === '' ? undefined : { number };
  }
  const number = typeof identity.number === 'string' ? identity.number.trim() : '';
  if (number === '') {
    return undefined;
  }
  const issuer = typeof identity.issuer === 'string' ? identity.issuer.trim() : '';
  const type = typeof identity.type === 'string' ? identity.type.trim() : '';
  return {
    number,
    issuer: issuer === '' ? undefined : issuer,
    type: type === '' ? undefined : type
  };
}

/**
 * Two identities are the same provider iff their numbers match AND neither `issuer` nor `type`
 * actively conflicts -- per CDMX-18465's explicit call: number is the ground truth, but `issuer`/
 * `type` only gate the match when BOTH sides actually carry a value for that attribute (the three
 * string-shaped number fields never have an issuer/type to compare, so they're always compatible-by-
 * number with an object-shaped identity; two object identities sharing a number but a genuinely
 * different issuer or type are NOT the same provider).
 */
function identityCompatible(
  a: { readonly issuer?: string; readonly type?: string },
  b: { readonly issuer?: string; readonly type?: string }
): boolean {
  const issuerConflicts =
    a.issuer != null && b.issuer != null && a.issuer !== b.issuer;
  const typeConflicts = a.type != null && b.type != null && a.type !== b.type;
  return !issuerConflicts && !typeConflicts;
}

/**
 * Looks up (or creates) the `MutableProviderEntry` for a role's identity. No usable identity ->
 * always a brand-new, never-merged entry (deliberately NOT matched by name -- two same-named people
 * with no number in common are treated as different providers). With a usable identity, entries are
 * bucketed by raw number string (`byNumber`) since the same number can legitimately belong to more
 * than one entry when `issuer`/`type` conflict; the bucket is scanned for a compatible entry before
 * a new one is created.
 */
function findOrCreateEntry(
  byNumber: Map<string, MutableProviderEntry[]>,
  order: MutableProviderEntry[],
  identity: NormalizedIdentity | undefined,
  name: string
): MutableProviderEntry {
  if (identity === undefined) {
    const entry: MutableProviderEntry = { name, isAttending: false, roles: [] };
    order.push(entry);
    return entry;
  }

  const bucket = byNumber.get(identity.number);
  const match = bucket?.find((candidate) =>
    identityCompatible(
      { issuer: candidate.identityIssuer, type: candidate.identityType },
      identity
    )
  );
  if (match) {
    match.identityIssuer = match.identityIssuer ?? identity.issuer;
    match.identityType = match.identityType ?? identity.type;
    return match;
  }

  const entry: MutableProviderEntry = {
    name,
    isAttending: false,
    roles: [],
    identityIssuer: identity.issuer,
    identityType: identity.type
  };
  if (bucket) {
    bucket.push(entry);
  } else {
    byNumber.set(identity.number, [entry]);
  }
  order.push(entry);
  return entry;
}

function addNamedRole(
  byNumber: Map<string, MutableProviderEntry[]>,
  order: MutableProviderEntry[],
  role: NamedRoleField
): void {
  const name = typeof role.name === 'string' ? role.name.trim() : '';
  if (name === '') {
    return;
  }
  const entry = findOrCreateEntry(
    byNumber,
    order,
    normalizeIdentity(role.identity),
    name
  );
  if (!entry.roles.some((existing) => existing.id === role.id)) {
    entry.roles.push({ id: role.id, label: role.label });
  }
  entry.credentials = preferFirst(entry.credentials, role.credentials);
  entry.providerType = preferFirst(entry.providerType, role.providerType);
  entry.specialty = preferFirst(entry.specialty, role.specialty);
}

/**
 * Folds `attending_provider_name`/`attending_provider_number` in LAST: flags an existing entry
 * `isAttending: true` when its identity matches one already collected from `addNamedRole` above (per
 * the same number-based rule -- never matched by name alone), or adds a new, role-less entry when the
 * attending provider is someone new. Returns the resolved entry so the caller can move it to the
 * front of the display order, or `undefined` when no attending provider is present at all.
 */
function foldInAttending(
  byNumber: Map<string, MutableProviderEntry[]>,
  order: MutableProviderEntry[],
  run: CMxAPI.CaseRun
): MutableProviderEntry | undefined {
  const attendingName =
    typeof run.attending_provider_name === 'string'
      ? run.attending_provider_name.trim()
      : '';
  if (attendingName === '') {
    return undefined;
  }
  const identity = normalizeIdentity(
    run.attending_provider_number as IdentityValue
  );
  const entry = findOrCreateEntry(byNumber, order, identity, attendingName);
  entry.specialty = preferFirst(
    entry.specialty,
    typeof run.attending_provider_specialty === 'string'
      ? run.attending_provider_specialty
      : undefined
  );
  entry.isAttending = true;
  return entry;
}

/**
 * The 5 roles a case's `invoices` array can carry, per invoice (`InvoicesAPI.Invoice` --
 * `billing`/`performing`'s type/specialty/credentials AND `referring`/`ordering`'s type/specialty are
 * all declared directly on the invoice; `rendering` has no type/credentials field on either source).
 */
function invoiceProviderRoles(invoice: InvoicesAPI.Invoice): NamedRoleField[] {
  return [
    {
      id: 'billing',
      label: 'Billing',
      name: invoice.billing_provider_name,
      providerType: invoice.billing_provider_type,
      credentials: invoice.billing_provider_credentials,
      specialty: invoice.billing_provider_specialty,
      identity: invoice.billing_provider_number
    },
    {
      id: 'performing',
      label: 'Performing provider',
      name: invoice.performing_provider_name,
      providerType: invoice.performing_provider_type,
      credentials: invoice.performing_provider_credentials,
      specialty: invoice.performing_provider_specialty,
      identity: invoice.performing_provider_number
    },
    {
      id: 'referring',
      label: 'Referring provider',
      name: invoice.referring_provider_name,
      providerType: invoice.referring_provider_type,
      specialty: invoice.referring_provider_specialty,
      identity: invoice.referring_provider_number
    },
    {
      id: 'ordering',
      label: 'Ordering provider',
      name: invoice.ordering_provider_name,
      providerType: invoice.ordering_provider_type,
      specialty: invoice.ordering_provider_specialty,
      identity: invoice.ordering_provider_number
    },
    {
      id: 'rendering',
      label: 'Rendering provider',
      name: invoice.rendering_provider_name,
      specialty: invoice.rendering_provider_specialty,
      identity: invoice.rendering_provider_number
    }
  ];
}

/**
 * The same 5 roles, sourced from the root case's flat fields -- used only when `run.invoices` is
 * empty/absent. `billing`/`performing`'s type/credentials are off-type (`extras`); the root case has
 * no `referring`/`ordering` type field at all (unlike the invoice, which declares both).
 */
function rootFallbackProviderRoles(
  run: CMxAPI.CaseRun,
  extras: CaseRunProviderExtras
): NamedRoleField[] {
  return [
    {
      id: 'billing',
      label: 'Billing',
      name: run.billing_provider_name,
      providerType: extras.billing_provider_type,
      credentials: extras.billing_provider_credentials,
      specialty: run.billing_provider_specialty,
      identity: run.billing_provider_number
    },
    {
      id: 'performing',
      label: 'Performing provider',
      name: run.performing_provider_name,
      providerType: extras.performing_provider_type,
      credentials: extras.performing_provider_credentials,
      specialty: run.performing_provider_specialty,
      identity: run.performing_provider_number as IdentityValue
    },
    {
      id: 'referring',
      label: 'Referring provider',
      name: run.referring_provider_name,
      specialty: run.referring_provider_specialty,
      identity: run.referring_provider_number as IdentityValue
    },
    {
      id: 'ordering',
      label: 'Ordering provider',
      name: run.ordering_provider_name,
      specialty: run.ordering_provider_specialty,
      identity: run.ordering_provider_number
    },
    {
      id: 'rendering',
      label: 'Rendering provider',
      name: run.rendering_provider_name,
      specialty: run.rendering_provider_specialty,
      identity: run.rendering_provider_number as IdentityValue
    }
  ];
}

/**
 * Map the primary `CMxAPI.CaseRun` (coding mode) to the source-agnostic provider contract. Per the
 * approved design mock (CDMX-18465), this DEDUPES by provider NUMBER, not name -- two providers can
 * share a name, so a name match alone is never enough (see `identityCompatible`/`findOrCreateEntry`)
 * -- rather than emitting one row per fixed role slot, so the popover shows one card per distinct
 * (by-number) person, each tagged with every named role they hold.
 *
 * `billing`/`performing`/`referring`/`ordering`/`rendering` are INVOICE-FIRST: when `run.invoices` is
 * a non-empty array, each invoice contributes its own set of these 5 roles (a case with several
 * invoices can have a different billing/performing/etc. provider per invoice); the root case's flat
 * fields are used only as a fallback when there are no invoices at all. The other 6 roles
 * (authoring/authenticator/finalSign/admitting/service/consulting) never appear on an invoice, so they
 * always read the root case. `attending_provider_name`/`_number` are folded in last: they flag an
 * existing entry `isAttending: true` when the identity matches, or add a bare (role-less) entry when
 * it names someone new -- and lead the list either way, matching the mock's "Attending" badge always
 * appearing on the first card.
 */
export function toCaseProvidersFromCaseRun(
  run: CMxAPI.CaseRun
): CaseProvidersData {
  const extras = run as unknown as CaseRunProviderExtras;

  // Guarded per ELEMENT, not just per container: a null entry would throw here and take the whole
  // coding-screen state mapping down with it. The declared type forbids it; the payload is network
  // data, so the declaration is not a runtime guarantee.
  const invoices = Array.isArray(run.invoices) ? run.invoices : [];
  const invoiceFirstRoles: NamedRoleField[] =
    invoices.length > 0
      ? invoices.flatMap((invoice) => invoiceProviderRoles(invoice))
      : rootFallbackProviderRoles(run, extras);

  const namedRoles: readonly NamedRoleField[] = [
    ...invoiceFirstRoles,
    {
      id: 'authoring',
      label: 'Authoring provider',
      name: run.primary_activity_provider_name,
      identity: run.primary_activity_provider_number
    },
    {
      id: 'authenticator',
      label: 'Authenticator',
      name: run.assigned_document_auth_name,
      identity: run.assigned_document_auth_number
    },
    {
      id: 'finalSign',
      label: 'Final sign provider',
      name: run.note_final_sign_provider_name,
      identity: run.note_final_sign_provider_number
    },
    {
      id: 'admitting',
      label: 'Admitting provider',
      name: extras.admitting_provider_name,
      identity: extras.admitting_provider_number
    },
    {
      id: 'service',
      label: 'Service provider',
      name: run.service_provider_name,
      specialty: run.service_provider_specialty,
      identity: run.service_provider_number as IdentityValue
    },
    {
      id: 'consulting',
      label: 'Consulting provider',
      name: extras.consulting_provider_name,
      identity: extras.consulting_provider_number
    }
  ];

  const byNumber = new Map<string, MutableProviderEntry[]>();
  const order: MutableProviderEntry[] = [];

  for (const role of namedRoles) {
    addNamedRole(byNumber, order, role);
  }

  const attendingEntry = foldInAttending(byNumber, order, run);

  const orderedEntries = attendingEntry
    ? [attendingEntry, ...order.filter((entry) => entry !== attendingEntry)]
    : order;

  return {
    providers: orderedEntries.map((entry) => ({
      name: entry.name,
      credentials: entry.credentials,
      providerType: entry.providerType,
      specialty: entry.specialty,
      isAttending: entry.isAttending,
      roles: entry.roles
    }))
  };
}

/**
 * Trimmed, non-empty string or `undefined`. `CaseRun`'s insurance fields arrive as `''` at least as
 * often as `null`, and a plain `?? undefined` would hand `PayerInfo` an empty string — an empty pill
 * rather than the placeholder.
 */
function cleanString(
  // Not `unknown`: the narrow signature is what lets the compiler reject a structurally wrong field
  // at the call site instead of silently returning `undefined` and rendering "N/A".
  value: string | number | null | undefined
): string | undefined {
  // `String(NaN)` is `'NaN'`, which `PayerInfo`'s `present()` accepts as real data and paints
  // verbatim in the pill.
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : undefined;
  }
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * `financial_status_class` ships on the runtime case run but is absent from the declared
 * `CMxAPI.CaseRun` type, so it is reached through the same narrow off-type cast
 * `toCaseSpecialtyFromCaseRun` uses rather than by editing the shared declaration.
 */
type CaseRunPayer = {
  financial_status_class?: string | null;
};

/**
 * Map the coding-mode case run to the payer contract. Only two of the four fields have a source:
 *
 *  - `payerPlan` reads `financial_status_class`, not the plan-typed `cmx_insurance_plan_text`, which
 *    is null on real cases. Platform-wide that field means *financial class*, so this is a known
 *    mild mislabel the product owner accepted as the only candidate carrying data.
 *  - `payerType` is deliberately NOT mapped from `primary_insurance_category`: on live data that
 *    field repeats `hl7_insurance_payer_id`, so binding it renders a raw payer ID where a
 *    classification belongs. Its real source is a dictionary lookup this pure mapper cannot host.
 *  - `rulesSummary` has no payer-keyed source anywhere in the platform.
 */
export function toPayerInfoFromCaseRun(run: CMxAPI.CaseRun): PayerInfoData {
  const off = run as unknown as CaseRunPayer;
  return {
    payerName: cleanString(run.primary_insurance),
    payerType: undefined,
    payerPlan: cleanString(off.financial_status_class),
    rulesSummary: undefined
  };
}

/**
 * Mirrors the shipped encounter view's `multi_place_of_service`
 * (`shared-views/src/views/patient-encounter/encounter-data.transformers.tsx`) for every valid
 * payload, so the two cannot disagree about the same case. Two deliberate differences on invalid
 * ones: this returns `undefined` rather than that transformer's literal `'N/A'` (the placeholder is
 * the slot component's job, and baking it in would make a genuine `'N/A'` in the feed
 * indistinguishable from an absent value), and it tolerates a malformed `invoices` where the
 * transformer throws.
 */
function invoicePlaceOfService(run: CMxAPI.CaseRun): string | undefined {
  const invoices = Array.isArray(run.invoices) ? run.invoices : [];
  // Deduped — several invoices on one case routinely share a place of service.
  const distinct = [
    ...new Set(
      invoices
        .map((invoice) =>
          invoice ? cleanString(invoice.place_of_service) : undefined
        )
        .filter((value): value is string => value !== undefined)
    )
  ];

  return distinct.length > 0 ? distinct.join(', ') : undefined;
}

/**
 * Three reads of the case JSON, and nothing else. No resolution, canonicalisation, or cross-field
 * substitution: a `patient_type` of `'11'` paints `11` — pinned by a regression test so it reads as
 * intentional. `cleanString` supplies the only normalisation there is: `null`, `''` and `'   '` all
 * mean absent, and surrounding whitespace is trimmed off a value that is kept.
 *
 * `invoice.patient_type` exists on the invoice type but is deliberately NOT read. Invoice-first
 * precedence applies to place of service alone, because that is the only field the shipped encounter
 * view resolves that way (`multi_place_of_service`); extending it here for symmetry would silently
 * disagree with that view.
 */
export function toPatientTypeInfoFromCaseRun(
  run: CMxAPI.CaseRun
): PatientTypeInfoData {
  return {
    patientType: cleanString(run.patient_type),
    legalStatus: cleanString(run.patient_class),
    // Invoice-first, matching the shipped encounter view.
    placeOfService:
      invoicePlaceOfService(run) ?? cleanString(run.place_of_service)
  };
}

/**
 * The grade-bench's `state.codeBench.activeItem` is DECLARED `CMxAPI.CaseRun`, but at runtime on the
 * CQA grading route (loaded by `loadCQAItemFromURL`) it is a CQA case record: snake_case, with the
 * account number in a flat `hospital_account_record` rather than the `account_number` CaseNumber the
 * coding CaseRun carries. Every OTHER field the grading mapper reads (`patient_name`, `patient_gender`,
 * `date_of_birth`, `date_of_service`, `case_id`) is already on the declared type — only this one is
 * off-type, so this narrow shape names just it, and the source is cast to it through `unknown`.
 */
type CQACaseRecord = {
  hospital_account_record?: string | null;
};

/**
 * Map the grade-bench case record (`state.codeBench.activeItem`) to the source-agnostic patient
 * contract. Age is derived from `date_of_birth` at `date_of_service` (same as the coding side).
 * Account # is the flat `hospital_account_record`. MRN and visit number have no source on this
 * record — those stay grading N/A gaps (`undefined` → 'N/A' at render). Fields normalize
 * `null → undefined`.
 */
export function toPatientInfoFromCodeBenchCase(
  activeItem: CMxAPI.CaseRun
): PatientInfoData {
  return {
    fullName: activeItem.patient_name ?? undefined,
    gender: activeItem.patient_gender ?? undefined,
    dob: activeItem.date_of_birth ?? undefined,
    age: ageAtService(activeItem.date_of_birth, activeItem.date_of_service),
    accountNumber:
      (activeItem as unknown as CQACaseRecord).hospital_account_record ??
      undefined,
    caseId: activeItem.case_id != null ? String(activeItem.case_id) : undefined,
    mrn: undefined,
    visitNumber: undefined
  };
}

/**
 * Source-agnostic contract `NoteNav`/`NoteViewer` (`apps/coding-screen/noteSlots/**`) read off
 * `state.values['doc.notes']`. `notes` is whatever `getDocumentsAsHtml` hydrated,
 * already shimmed/sorted by the SAME `GET_HTML/fulfilled` reducer the existing grade-bench Notes
 * experience reads (`code-bench.reducer.ts` -> `state.codeBench.notes`); this mapper does not
 * re-sort or re-shim, only carries the array through.
 */
export interface NotesData {
  readonly notes: readonly CMxCommonApp.DecoratedNote[];
}

/**
 * Build the coding-mode `CodingScreenState` (`case.*` namespace). `notes` is OPTIONAL
 * and defaults to `[]` -- it does not come off `run` (a `CMxAPI.CaseRun`) at all, but off the
 * SEPARATE `getDocumentsAsHtml`-hydrated redux slice (`state.codeBench.notes`), which the caller
 * (`coding-screen.component.ts`'s mapState) reads and passes in alongside the primary `CaseRun`.
 * `doc.notes` is a SHARED namespace (not `case.*`/`workitem.*`), so it carries no mode restriction
 * under `validateConfig`'s gate.
 *
 * `notesCount`/`activityCount` are unrelated to `notes` despite the name overlap, and come off
 * DIFFERENT slices again -- `state.coderNotes.coderNotesList` and `codeBench.activity` -- feeding the
 * Notes/Activity tab counts rather than the document pane. All three are passed in by the same
 * mapState caller.
 *
 * `case.identifier` is the numeric DB `case_id`, stringified (owner decision). It
 * has no `componentId`, so it MUST be a primitive and is set only when defined: an absent key reads
 * back `undefined` and renders empty, while a non-primitive would throw in `renderPlainText`.
 */
export function codingStateFromCaseRun(
  run: CMxAPI.CaseRun,
  notes: readonly CMxCommonApp.DecoratedNote[] = [],
  notesCount = 0,
  activityCount = 0
): CodingScreenState {
  const values: Record<string, unknown> = {
    'case.patient': toPatientInfoFromCaseRun(run),
    'doc.notes': { notes } satisfies NotesData,
    'case.specialty': toCaseSpecialtyFromCaseRun(run),
    // Structured, so its slot MUST keep `componentId: 'PatientTypeInfo'` — same rule as `case.payer`
    // below. Written unconditionally: all three of its fields are sourced, so an absent value is a
    // rendered `N/A` rather than a missing field.
    'case.patientType': toPatientTypeInfoFromCaseRun(run),
    // Structured, so its slot MUST keep `componentId: 'PayerInfo'` — a structured value on a
    // componentId-less slot throws in `renderPlainText` by design.
    'case.payer': toPayerInfoFromCaseRun(run),
    'case.providers': toCaseProvidersFromCaseRun(run),
    'case.billingLocation': toBillingLocationFromCaseRun(run),
    'case.notesCount': notesCount,
    'case.activityCount': activityCount,
    // Must EXIST for the slot to resolve at all (`readState` returns undefined for an absent key), and is
    // empty because nothing here can source the entitlement, dictionary or identity honestly.
    'case.excludeContext': {},
    // the ONE aggregate value the Transfer slot binds (a slot has exactly one
    // contentId, and the modal needs both the acting identity and the pickable data).
    //  - `currentUser` is still unresolved on this route (the same gap Hold's "Logged as" has --
    //    the shell has no session source yet), so the modal shows its placeholder until that lands.
    //  - `otherReasons` is deliberately ABSENT: no Transfer Reason dictionary exists anywhere in
    //    the platform, so the "Other" pill renders disabled and explains itself rather than
    //    accepting an unqualified reason.
    //  - `permission` is likewise unresolved: the real check is role + originating-worklist-view
    //    based and lives in service-desk, which this app may not reach from here. Safe to omit only
    //    while no write path exists -- wiring it fail-closed is a blocking precondition on the
    //    transfer-write follow-up.
    // Recipients are NOT seeded here: they come from the live directory search on the api.
    'case.transfer': {}
  };
  if (run.case_id != null) {
    values['case.identifier'] = String(run.case_id);
  }
  return { values };
}

/**
 * Build the grading-mode `CodingScreenState` (`workitem.*` namespace) from the codeBench case record.
 * `case_id` is already on the declared `CMxAPI.CaseRun`, so it is read directly (no cast). Same
 * primitive-only / set-when-defined rule for `workitem.identifier` as `case.identifier`.
 */
export function gradingStateFromCodeBenchCase(
  activeItem: CMxAPI.CaseRun,
  activityCount = 0
): CodingScreenState {
  const values: Record<string, unknown> = {
    'workitem.patient': toPatientInfoFromCodeBenchCase(activeItem),
    'workitem.specialty': toCaseSpecialtyFromCaseRun(activeItem),
    'workitem.billingLocation': toBillingLocationFromCaseRun(activeItem),
    'case.activityCount': activityCount
  };
  if (activeItem.case_id != null) {
    values['workitem.identifier'] = String(activeItem.case_id);
  }
  return { values };
}
